import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
} from "react-router-dom";

import { BackupOtpPage } from "../pages/auth/backup-otp/page";
import { CoachIntegration } from "../pages/auth/coach-integration/page";
import { ForgotPasswordPage } from "../pages/auth/forgot-password/page";
import { AuthLayout } from "../pages/auth/layout";
import { LoginPage } from "../pages/auth/login/page";
import { RegisterPage } from "../pages/auth/register/page";
import { ResetPasswordPage } from "../pages/auth/reset-password/page";
import { VerifyEmailPage } from "../pages/auth/verify-email/page";
import { VerifyOtpPage } from "../pages/auth/verify-otp/page";
import { BuilderLayout } from "../pages/builder/layout";
import { BuilderPage } from "../pages/builder/page";
import { builderLoader } from "../pages/builder/page";
import { CoachOptimizePage } from "../pages/coach/optimize/page";
import { AiOptimizationPage } from "../pages/dashboard/ai-optimization/page";
import { DashboardLayout } from "../pages/dashboard/layout";
import { ResumesPage } from "../pages/dashboard/resumes/page";
import { SettingsPage } from "../pages/dashboard/settings/page";
import { HomeLayout } from "../pages/home/layout";
import { HomePage } from "../pages/home/page";
import { publicLoader, PublicResumePage } from "../pages/public/page";
import { Providers } from "../providers";
import { AuthGuard } from "./guards/auth";
import { GuestGuard } from "./guards/guest";
import { authLoader } from "./loaders/auth";
const googleRecaptcha = "6LdQ59UmAAAAADnGYBOuX3THdCX_n3IrN6mv3GAI";

// Gates /dashboard/ai-optimization to Masters Union hosts (plus localhost, so the route stays
// reachable in local development). Mirrors the domain check used in the sidebar
// (isMastersUnionDomain) and elsewhere (resumes/_dialogs/resume.tsx, builder/sidebars/right/
// sections/template.tsx). Inlined here since this is its only usage.
const isMastersUnionAllowedHost = () =>
  typeof window !== "undefined" &&
  (window.location.hostname.includes("mastersunion") || window.location.hostname === "localhost");

const MastersUnionGuard = () =>
  isMastersUnionAllowedHost() ? <Outlet /> : <Navigate to="/dashboard/resumes" replace />;

export const routes = createRoutesFromElements(
  <Route element={<Providers />}>
    <Route element={<HomeLayout />}>
      <Route path="/" element={<HomePage />} />
    </Route>

    <Route path="auth">
      <Route element={<AuthLayout />}>
        <Route element={<GuestGuard />}>
          <Route
            path="login"
            element={
              <GoogleReCaptchaProvider reCaptchaKey={googleRecaptcha}>
                <LoginPage />
              </GoogleReCaptchaProvider>
            }
          />
          <Route path="register" element={<RegisterPage />} />
        </Route>

        {/* Password Recovery */}
        <Route element={<GuestGuard />}>
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
        </Route>

        {/* LMS Integration.
             Deliberately NOT behind GuestGuard: this is an SSO entry point, and the
             token decides which account gets signed in. Guarding it sent an
             already-logged-in student to /dashboard without ever running the
             handshake — so a Coach Resume link silently lost its job context, and a
             link for a DIFFERENT account would have been ignored while the previous
             session stayed active. */}
        <Route path="coach-integration" element={<CoachIntegration />} />

        {/* Two-Factor Authentication */}
        <Route element={<GuestGuard />}>
          <Route path="verify-otp" element={<VerifyOtpPage />} />
          <Route path="backup-otp" element={<BackupOtpPage />} />
        </Route>

        {/* Email Verification */}
        <Route element={<AuthGuard />}>
          <Route path="verify-email" element={<VerifyEmailPage />} />
        </Route>

        {/* OAuth Callback */}
        <Route path="callback" loader={authLoader} />
      </Route>

      <Route index element={<Navigate to="/auth/login" replace />} />
    </Route>

    <Route path="dashboard">
      <Route element={<AuthGuard />}>
        <Route element={<DashboardLayout />}>
          <Route path="resumes" element={<ResumesPage />} />
          <Route element={<MastersUnionGuard />}>
            <Route path="ai-optimization" element={<AiOptimizationPage />} />
          </Route>
          <Route path="settings" element={<SettingsPage />} />

          <Route index element={<Navigate to="/dashboard/resumes" replace />} />
        </Route>
      </Route>
    </Route>

    {/* Coach Resume optimization intake (entered from the LMS handshake) */}
    <Route path="coach">
      <Route element={<AuthGuard />}>
        <Route path="optimize" element={<CoachOptimizePage />} />

        <Route index element={<Navigate to="/dashboard/resumes" replace />} />
      </Route>
    </Route>

    <Route path="builder">
      <Route element={<AuthGuard />}>
        <Route element={<BuilderLayout />}>
          <Route path=":id" loader={builderLoader} element={<BuilderPage />} />

          <Route index element={<Navigate to="/dashboard/resumes" replace />} />
        </Route>
      </Route>
    </Route>

    {/* Public Routes */}
    <Route path=":username">
      <Route path=":slug" loader={publicLoader} element={<PublicResumePage />} />
    </Route>
  </Route>,
);

export const router = createBrowserRouter(routes);
